import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { wibToday, addDays, firstOfMonth, firstOfYear } from "@/lib/bizdate";
import { CalendarDays, ChevronDown } from "lucide-react";

const PRESETS = [
  { key: "today", label: "Hari Ini" },
  { key: "7d", label: "7 Hari Terakhir" },
  { key: "month", label: "Bulan Ini" },
  { key: "ytd", label: "Tahun Ini" },
];

// KPI suffix per period
export const PERIOD_SUFFIX = { today: "Hari Ini", "7d": "7 Hari", month: "Bulan Ini", ytd: "Tahun Ini", custom: "Periode" };

const toDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export function usePeriod(def_ = "today") {
  const [sp, setSp] = useSearchParams();
  const period = sp.get("period") || def_;
  const start_date = sp.get("start_date") || "";
  const end_date = sp.get("end_date") || "";
  const setPeriod = (p, s, e) => {
    const next = new URLSearchParams(sp);
    next.set("period", p);
    if (p === "custom") { next.set("start_date", s); next.set("end_date", e); }
    else { next.delete("start_date"); next.delete("end_date"); }
    setSp(next, { replace: true });
  };
  return { period, start_date, end_date, setPeriod };
}

// Compute the effective inclusive date range (ISO) for display — in Asia/Jakarta (WIB)
export function effectiveRange(period, start_date, end_date) {
  const today = wibToday();
  if (period === "custom") return [start_date, end_date];
  if (period === "7d") return [addDays(today, -6), today];
  if (period === "month") return [firstOfMonth(today), today];
  if (period === "ytd") return [firstOfYear(today), today];
  return [today, today];
}

export default function PeriodFilter({ period, start_date, end_date, onChange }) {
  const [open, setOpen] = useState(false);
  const [tmp, setTmp] = useState({ s: start_date || "", e: end_date || "" });
  const [err, setErr] = useState("");
  const [from, to] = effectiveRange(period, start_date, end_date);

  const apply = () => {
    if (!tmp.s) return setErr("Tanggal mulai wajib diisi");
    if (!tmp.e) return setErr("Tanggal akhir wajib diisi");
    if (tmp.s > tmp.e) return setErr("Tanggal mulai tidak boleh setelah tanggal akhir");
    setErr("");
    onChange("custom", tmp.s, tmp.e);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="period-filter">
      <div className="inline-flex flex-wrap items-center gap-1.5">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => onChange(p.key)}
              data-testid={`period-${p.key}`}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                period === p.key ? "bg-indigo-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setTmp({ s: start_date || from, e: end_date || to }); }}>
          <PopoverTrigger asChild>
            <button
              data-testid="period-custom"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors",
                period === "custom" ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
              )}
            >
              <CalendarDays className="h-4 w-4" /> Pilih Tanggal <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72" data-testid="period-custom-popover">
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Tanggal Mulai</Label>
                <Input type="date" className="mt-1" value={tmp.s} max={tmp.e || undefined} onChange={(e) => setTmp({ ...tmp, s: e.target.value })} data-testid="period-start" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Tanggal Akhir</Label>
                <Input type="date" className="mt-1" value={tmp.e} min={tmp.s || undefined} onChange={(e) => setTmp({ ...tmp, e: e.target.value })} data-testid="period-end" />
              </div>
              {err && <p className="text-xs font-medium text-rose-600" data-testid="period-error">{err}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="period-cancel">Batal</Button>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-500" onClick={apply} data-testid="period-apply">Terapkan</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <p className="text-xs text-slate-500" data-testid="period-range">
        Periode: <span className="font-semibold text-slate-700">{toDDMMYYYY(from)} – {toDDMMYYYY(to)}</span>
      </p>
    </div>
  );
}
